/**
 * Preview Modal Component
 * 
 * Modal for previewing the form as it will appear to users.
 * Shows one step at a time with navigation.
 */

import { useState } from 'react';
import { X, Monitor, Tablet, Smartphone, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFormBuilder } from '../../contexts';
import './PreviewModal.css';

interface PreviewModalProps {
  onClose: () => void;
}

type DeviceSize = 'desktop' | 'tablet' | 'mobile';

const deviceSizes: Record<DeviceSize, { width: number; label: string; icon: React.ReactNode }> = {
  desktop: { width: 900, label: 'Desktop', icon: <Monitor size={16} /> },
  tablet: { width: 768, label: 'Tablet', icon: <Tablet size={16} /> },
  mobile: { width: 375, label: 'Mobile', icon: <Smartphone size={16} /> },
};

export function PreviewModal({ onClose }: PreviewModalProps) {
  const { state } = useFormBuilder();
  const { form } = state;
  const [device, setDevice] = useState<DeviceSize>('desktop');
  const [key, setKey] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const handleRefresh = () => {
    setKey((k) => k + 1);
    setCurrentStepIndex(0);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get steps from form
  const steps = form?.active_version?.steps || [];
  const hasSteps = steps.length > 0;
  const totalSteps = steps.length;
  const currentStep = steps[currentStepIndex];

  // Navigation handlers
  const goToNextStep = () => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  return (
    <div className="modal-backdrop preview-backdrop" onClick={handleBackdropClick}>
      <div className="preview-modal">
        <div className="modal-header">
          <h2>Form Preview</h2>
          <div className="device-selector">
            {(Object.keys(deviceSizes) as DeviceSize[]).map((d) => (
              <button
                key={d}
                className={`device-btn ${device === d ? 'active' : ''}`}
                onClick={() => setDevice(d)}
                title={deviceSizes[d].label}
              >
                {deviceSizes[d].icon}
              </button>
            ))}
          </div>
          <div className="header-actions">
            <button
              className="btn-icon"
              onClick={handleRefresh}
              title="Refresh preview"
            >
              <RefreshCw size={18} />
            </button>
            <button className="btn-close" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="preview-container">
          <div
            className="preview-frame"
            style={{ maxWidth: deviceSizes[device].width }}
          >
            {hasSteps ? (
              <div className="preview-content" key={key}>
                <div className="form-preview">
                  {/* Form Header */}
                  <div className="form-preview-header">
                    <h3>{form?.name}</h3>
                    {form?.description && <p>{form.description}</p>}
                  </div>

                  {/* Current Step Fields */}
                  {currentStep && (
                    <div className="preview-step-content">
                      {currentStep.variants[0]?.fields.length === 0 ? (
                        <div className="preview-step-empty">
                          <p>No fields in this step yet</p>
                        </div>
                      ) : (
                        currentStep.variants[0]?.fields.map((field) => (
                          <div
                            key={field.id}
                            className={`preview-field ${field.is_phi ? 'phi' : ''}`}
                          >
                            <label>
                              {field.label}
                              {field.required && <span className="required">*</span>}
                            </label>

                            {field.field_type === 'text' && (
                              <input
                                type="text"
                                placeholder={field.placeholder || 'Enter text...'}
                                disabled
                              />
                            )}

                            {field.field_type === 'email' && (
                              <input
                                type="email"
                                placeholder={field.placeholder || 'Enter email...'}
                                disabled
                              />
                            )}

                            {field.field_type === 'phone' && (
                              <input
                                type="tel"
                                placeholder={field.placeholder || 'Enter phone...'}
                                disabled
                              />
                            )}

                            {field.field_type === 'number' && (
                              <input
                                type="number"
                                placeholder={field.placeholder || 'Enter number...'}
                                disabled
                              />
                            )}

                            {field.field_type === 'textarea' && (
                              <textarea
                                placeholder={field.placeholder || 'Enter text...'}
                                rows={3}
                                disabled
                              />
                            )}

                            {field.field_type === 'date' && (
                              <input type="date" disabled />
                            )}

                            {field.field_type === 'time' && (
                              <input type="time" disabled />
                            )}

                            {field.field_type === 'datetime' && (
                              <input type="datetime-local" disabled />
                            )}

                            {field.field_type === 'select' && (
                              <select disabled>
                                <option>{field.placeholder || 'Select...'}</option>
                                {field.options?.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            )}

                            {field.field_type === 'radio' && (
                              <div className="radio-group">
                                {field.options?.map((opt) => (
                                  <label key={opt.value} className="radio-label">
                                    <input
                                      type="radio"
                                      name={field.name}
                                      value={opt.value}
                                      disabled
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {field.field_type === 'checkbox' && (
                              <div className="checkbox-group">
                                {field.options?.map((opt) => (
                                  <label key={opt.value} className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      value={opt.value}
                                      disabled
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            )}

                            {field.help_text && (
                              <span className="help-text">{field.help_text}</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Navigation / Submit */}
                  <div className="preview-actions">
                    {totalSteps > 1 ? (
                      <div className="preview-nav">
                        <button 
                          className="preview-nav-btn preview-nav-prev"
                          onClick={goToPrevStep}
                          disabled={isFirstStep}
                        >
                          <ChevronLeft size={18} />
                          Previous
                        </button>
                        
                        <span className="preview-step-indicator">
                          Step {currentStepIndex + 1} of {totalSteps}
                        </span>
                        
                        {isLastStep ? (
                          <button className="preview-submit" disabled>
                            Submit
                          </button>
                        ) : (
                          <button 
                            className="preview-nav-btn preview-nav-next"
                            onClick={goToNextStep}
                            disabled={isLastStep}
                          >
                            Next
                            <ChevronRight size={18} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <button className="preview-submit" disabled>
                        Submit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="preview-empty">
                <p>Add steps and fields to preview your form</p>
              </div>
            )}
          </div>
        </div>

        <div className="preview-footer">
          <span className="preview-note">
            This is a preview. Submissions are disabled.
          </span>
        </div>
      </div>
    </div>
  );
}

export default PreviewModal;
